import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { ProductVariation, CalendarCustomization } from '../types';
import { BreadcrumbItem } from '../types';
import { useShopping } from '../contexts/ShoppingContext';
import { QuantitySelector, Button, ImageGallery, Breadcrumb } from './ui';
import { BulkOrderTable } from './BulkOrderTable';
import { CalendarCustomizer } from './CalendarCustomizer';
import colorMap from '../data/colors.json';
import {formatPrice} from "../utils/formatPrice.ts";

function getTextColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

export function ProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { state, dispatch, loading } = useShopping();
  const [selectedVariation, setSelectedVariation] = useState<ProductVariation | null>(null);
  const product = state.products.find(p => p.id === Number(productId));
  const minQty = product?.minOrderQty && product.minOrderQty > 1 ? product.minOrderQty : 1;
  const [quantity, setQuantity] = useState(minQty);
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);
  const [calendarCustomization, setCalendarCustomization] = useState<CalendarCustomization | undefined>();

  // For products without variations, create a default variation using product-level stock
  const variation = selectedVariation || product?.variations[0] || (
    product?.variations.length === 0 && product?.stock !== null && product?.stock !== undefined
      ? {
          id: `${product.id}-default`,
          color: "",
          size: "",
          price: product.basePrice,
          stock: product.stock,
          images: product.images
        }
      : undefined
  );

  // Build breadcrumb items
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', href: '/' }
  ];

  // Get department and category from product
  if (product && product.categories.length > 0) {
    const categoryId = product.categories[0];
    const category = state.departments
      .flatMap(dept => dept.productCategories || [])
      .find(cat => cat.id === categoryId);

    if (category) {
      // Find the department that contains this category
      const department = state.departments.find(d =>
        d.productCategories?.some(c => c.id === categoryId)
      );

      if (department) {
        breadcrumbItems.push({
          label: department.name,
          href: `/?departmentId=${department.id}`
        });
      }

      const categoryHref = department
        ? `/?departmentId=${department.id}&categoryId=${category.id}`
        : `/?categoryId=${category.id}`;

      breadcrumbItems.push({
        label: category.name,
        href: categoryHref
      });
    }
  }

  if (product) {
    breadcrumbItems.push({
      label: product.name
    });
  }

  // Get current images based on hovered or selected variation
  const getCurrentImages = () => {
    // If hovering over a color, show that variation's images
    if (hoveredColor && product) {
      const hoveredVariation = product.variations.find(v =>
        v.color === hoveredColor && (selectedVariation ? v.size === selectedVariation.size : true)
      );
      if (hoveredVariation?.images && hoveredVariation.images.length > 0) {
        return hoveredVariation.images;
      }
    }

    // Otherwise show selected variation images
    if (variation?.images && variation.images.length > 0) {
      return variation.images;
    }
    return product?.images || [];
  };

  useEffect(() => {
    // Only redirect if loading is complete and product doesn't exist
    if (!loading && !product) {
      navigate('/');
      return;
    }
    if (product && !selectedVariation) {
      // Only set selected variation if product has variations
      // Products without variations will use the default variation created above
      if (product.variations.length > 0) {
        setSelectedVariation(product.variations[0]);
      }
    }
    // Reset quantity to minimum when product changes
    if (product) {
      const newMinQty = product.minOrderQty && product.minOrderQty > 1 ? product.minOrderQty : 1;
      setQuantity(newMinQty);
    }
  }, [product, navigate, loading, selectedVariation]);

  // Show loading state while products are being fetched
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const handleAddToCart = () => {
    if (variation) {
      if (product.isPrintDetail && !calendarCustomization) {
        alert('Please customize your calendar before adding to cart');
        return;
      }
      dispatch({
        type: 'ADD_TO_CART',
        payload: { product, variation, quantity, calendarCustomization }
      });
    }
  };

  const handleBulkAddToCart = (items: Array<{ variation: ProductVariation; quantity: number }>) => {
    items.forEach(({ variation, quantity }) => {
      dispatch({
        type: 'ADD_TO_CART',
        payload: { product, variation, quantity }
      });
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <Breadcrumb items={breadcrumbItems} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Image Gallery */}
        <div className="lg:max-w-md">
          <ImageGallery
            images={getCurrentImages()}
            alt={product.name}
            className="rounded-lg"
            showThumbnails={true}
            enableKeyboardNavigation={true}
          />
        </div>

        {/* Product Details */}
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {product.name}
            </h1>
          </div>

          {/* Price */}
          <div className="border-t border-gray-200 pt-4">
            <span className="text-3xl font-bold text-gray-900">
              ${formatPrice(variation?.price || product.basePrice)}
            </span>
          </div>

          {/* Variations */}
          <div className="space-y-4">
            {/* Size Selection */}
            {product.variations
                    .filter(v => v.size.trim() !== '')
                    .some(v => v.size !== 'Standard')
                && (
              <div>
                <h3 className="text-base font-medium text-gray-900 mb-2">Size</h3>
                <div className="grid grid-cols-4 gap-2">
                  {Array.from(
                      new Set(product.variations
                          .filter(v => v.size.trim() !== '')
                          .map(v => v.size))
                  ).map(size => {
                    // Check if any variation with this size has stock or if backorder/on-demand is enabled
                    const hasStock = product.allowBackorder || product.allowOnDemand ||
                      product.variations.some(v => v.size === size && v.stock > 0);
                    const showOutOfStock = !hasStock && !product.allowOnDemand;

                    return (
                      <button
                        key={size}
                        onClick={() => {
                          const newVariation = product.variations.find(v =>
                            v.size === size && (selectedVariation ? v.color === selectedVariation.color : true)
                          ) || product.variations.find(v => v.size === size);
                          setSelectedVariation(newVariation || null);
                        }}
                        disabled={!hasStock}
                        className={`py-2 px-3 text-sm border rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          !hasStock
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : variation?.size === size
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        aria-label={`Select size ${size}${showOutOfStock ? ' (out of stock)' : ''}`}
                        aria-pressed={variation?.size === size}
                        aria-disabled={!hasStock}
                      >
                        {!hasStock ? <span className="line-through">{size}</span> : size}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {(() => {
              const colors = Array.from(new Set(product.variations.map(v => v.color))).filter(c => c && c !== 'Default');
              return colors.length > 0 ? (
                <div>
                  <h3 className="text-base font-medium text-gray-900 mb-2">Color</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {colors.map(color => {
                      // Check if any variation with this color has stock or if backorder/on-demand is enabled
                      const hasStock = product.allowBackorder || product.allowOnDemand ||
                        product.variations.some(v => v.color === color && v.stock > 0);
                      const showOutOfStock = !hasStock && !product.allowOnDemand;

                      const bgColor = colorMap[color as keyof typeof colorMap] || '#E5E7EB';
                      const textColor = getTextColor(bgColor);

                      return (
                        <button
                          key={color}
                          onClick={() => {
                            const newVariation = product.variations.find(v =>
                              v.color === color && (selectedVariation ? v.size === selectedVariation.size : true)
                            ) || product.variations.find(v => v.color === color);
                            setSelectedVariation(newVariation || null);
                          }}
                          onMouseEnter={() => !hasStock ? null : setHoveredColor(color)}
                          onMouseLeave={() => setHoveredColor(null)}
                          disabled={!hasStock}
                          style={{
                            backgroundColor: hasStock ? bgColor : '#F3F4F6',
                            color: hasStock ? textColor : '#9CA3AF',
                            borderColor: variation?.color === color ? '#2563EB' : '#D1D5DB',
                            opacity: hasStock ? 1 : 0.6
                          }}
                          className={`py-2 px-3 text-sm border rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            !hasStock
                              ? 'cursor-not-allowed'
                              : variation?.color === color
                              ? 'ring-2 ring-blue-600'
                              : 'hover:border-gray-400'
                          }`}
                          aria-label={`Select ${color} color${showOutOfStock ? ' (out of stock)' : ''}`}
                          aria-pressed={variation?.color === color}
                          aria-disabled={!hasStock}
                        >
                          {!hasStock ? <span className="line-through">{color}</span> : color}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null;
            })()}
          </div>

          {/* Quantity */}
          <div>
            <h3 className="text-base font-medium text-gray-900 mb-2">
              Quantity
              {product.minOrderQty != null && product.minOrderQty > 1 && (
                <span className="ml-2 text-sm text-gray-500">(Min: {product.minOrderQty})</span>
              )}
            </h3>
            <QuantitySelector
              quantity={quantity}
              onQuantityChange={setQuantity}
              min={minQty}
              size="md"
              stock={variation?.stock}
              showStock={true}
              allowManualInput={true}
              allowOnDemand={product.allowOnDemand}
            />
          </div>

          {/* Add to Cart Button */}
          <div className="border-t border-gray-200 pt-4">
            <Button
              onClick={handleAddToCart}
              disabled={!variation || (!product.allowBackorder && !product.allowOnDemand && variation.stock < quantity)}
              variant="primary"
              size="md"
              fullWidth={true}
              icon={<ShoppingCart className="w-5 h-5" />}
            >
              Add to Cart
            </Button>
          </div>
        </div>
      </div>

      {/* Calendar Customization */}
      {product.isPrintDetail && (
        <div className="mt-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Customize Your Calendar</h2>
          <CalendarCustomizer
            onCustomizationChange={setCalendarCustomization}
            initialCustomization={calendarCustomization}
            variation={variation}
            productImages={product.images}
          />
        </div>
      )}

      {/* Full Description below image */}
      <div className="mt-6">
        <h3 className="text-base font-medium text-gray-900 mb-2">Description</h3>
        <div
          className="text-sm text-gray-600 leading-relaxed [&>p]:mb-4 [&>ul]:list-disc [&>ul]:ml-6 [&>ol]:list-decimal [&>ol]:ml-6 [&>strong]:font-semibold [&>em]:italic"
          dangerouslySetInnerHTML={{ __html: product.description }}
        />
      </div>

      {/* Bulk Order Table */}
      {product.variations.length > 0 && (
        <div className="mt-8">
          <BulkOrderTable product={product} onAddToCart={handleBulkAddToCart} />
        </div>
      )}
    </div>
  );
}
